import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComparisonSelectionTotals } from '@/types/chat/chat';
import {
  counterparts,
  roleCandidates,
  type ComparisonChoice,
  type ComparisonPurpose,
} from './comparisonRoles';
import type { ComparisonQuota } from './quota';
import QuotaBadge, { type QuotaBadgeHandle } from './QuotaBadge';
import styles from './composer.module.css';

export interface ComparisonSetupHandle {
  /** Move focus to the role selector (a blocking reason's resolution). */
  focusRole: () => void;
  showBreakdown: () => void;
}

/**
 * Comparison purpose and roles in the composer (spec §4). Document changes:
 * one baseline among the separately registered documents; the others are
 * compared with it. Check against guidelines: one document to check, the
 * other documents are its reference guidelines; never one shared Baseline
 * label. The quota and its breakdown are always shown.
 */
const ComparisonSetup = forwardRef<
  ComparisonSetupHandle,
  {
    choice: ComparisonChoice;
    onChange: (choice: ComparisonChoice) => void;
    totals: ComparisonSelectionTotals | null | undefined;
    quota: ComparisonQuota | null;
    disabled?: boolean;
  }
>(({ choice, onChange, totals, quota, disabled }, ref) => {
  const { t } = useTranslation();
  const role = useRef<HTMLSelectElement>(null);
  const badge = useRef<QuotaBadgeHandle>(null);
  useImperativeHandle(
    ref,
    () => ({
      focusRole: () => role.current?.focus(),
      showBreakdown: () => badge.current?.open(),
    }),
    [],
  );
  const candidates = roleCandidates(totals);
  const others = counterparts(choice, candidates);
  const changes = choice.purpose === 'changes';
  const roleKey = changes ? 'baselineId' : 'documentToCheckId';
  const selected = choice[roleKey];

  return (
    <div className={styles.setup} data-testid="comparison-setup">
      {quota && totals ? (
        <QuotaBadge ref={badge} quota={quota} totals={totals} />
      ) : null}
      <label className={styles.setupField}>
        <span>{t('v2.composer.cmpPurpose')}</span>
        <select
          value={choice.purpose}
          disabled={disabled}
          onChange={event =>
            onChange({
              ...choice,
              purpose: event.target.value as ComparisonPurpose,
            })
          }
          data-testid="comparison-purpose"
        >
          <option value="changes">{t('v2.composer.cmpVersion')}</option>
          <option value="guidelines">{t('v2.composer.cmpRefCheck')}</option>
        </select>
      </label>
      <label className={styles.setupField}>
        <span>
          {changes
            ? t('v2.composer.cmpBaseline')
            : t('v2.comparison.cmpDocToCheck')}
        </span>
        <select
          ref={role}
          value={selected ?? ''}
          disabled={disabled}
          aria-invalid={selected == null || undefined}
          onChange={event =>
            onChange({
              ...choice,
              [roleKey]: event.target.value ? Number(event.target.value) : null,
            })
          }
          data-testid="comparison-role"
        >
          <option value="">
            {changes
              ? t('v2.composer.rBaseline')
              : t('v2.composer.rDocToCheck')}
          </option>
          {candidates.map(file => (
            <option key={file.id} value={file.id}>
              {file.name ?? t('v2.composer.unnamedSource', { id: file.id })}
            </option>
          ))}
        </select>
      </label>
      {others.length ? (
        <div className={styles.setupOthers} data-testid="comparison-others">
          <span className={styles.setupOthersLabel}>
            {changes
              ? t('v2.composer.cmpTargets', { n: others.length })
              : t('v2.comparison.cmpRefGuidelines')}
          </span>
          <ul>
            {others.map(file => (
              <li key={file.id}>
                {file.name ?? t('v2.composer.unnamedSource', { id: file.id })}
              </li>
            ))}
          </ul>
          {!changes ? (
            <p className={styles.note}>{t('v2.composer.cmpRefAllNote')}</p>
          ) : null}
        </div>
      ) : null}
      {!changes ? (
        <p className={styles.note} data-testid="comparison-disclaimer">
          {t('v2.comparison.cmpDisclaimer')}
        </p>
      ) : null}
    </div>
  );
});

ComparisonSetup.displayName = 'ComparisonSetup';

export default ComparisonSetup;
